import React, { useState, useEffect } from 'react';
import { Modal, Input, InputNumber, Form, Button, Select, Row, Col, Checkbox, Switch } from 'antd';
import ToolService from '../../services/ToolService';
import OfferingService from '../../services/OfferingService';
import { listGroupsAll } from '../../modules/groups/services/groupService';
import {
    PARAMETER_UNIT_OPTIONS,
    PARAMETER_PERIOD_OPTIONS,
    PARAMETER_UNIT_DEFAULT,
    PARAMETER_PERIOD_DEFAULT,
} from '../../utils';

const { Option } = Select;

export const PARAMETER_ACCREDITATION_DEFAULTS = {
    accreditationRequired: false,
    accredited: false,
};

export const resolveParameterAccreditationValues = (parameter?: any) => ({
    accreditationRequired: parameter?.accreditationRequired ?? false,
    accredited: parameter?.accredited ?? false,
});

const ParameterModal: React.FC<any> = ({
    visible,
    onClose,
    onSave,
    initialData,
    handleSave,
}) => {
    const [form] = Form.useForm();
    const isEdit = Boolean(initialData);
    const [tools, setTools] = useState<any[]>([]);
    const [offerings, setOfferings] = useState<any[]>([]);
    const [groups, setGroups] = useState<any[]>([]);
    const defaultUnit = PARAMETER_UNIT_DEFAULT;
    const defaultPeriod = PARAMETER_PERIOD_DEFAULT;
    // Aktif grupları listele; düzenlemede mevcut (pasif) grup da görünsün
    const groupOptions = React.useMemo(() => {
        const actives = (groups || []).filter((g: any) => g?.isActive === true);
        if (isEdit && initialData) {
            const currentId = (initialData.group && initialData.group.id) ?? initialData.groupId;
            if (currentId && !actives.some((g: any) => g.id === currentId)) {
                const found = (groups || []).find((g: any) => g.id === currentId);
                if (found) return [found, ...actives];
            }
        }
        return actives;
    }, [groups, isEdit, initialData]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [toolResponse, offeringResponse, groupsResponse] = await Promise.all([
                    ToolService.getAllTools(),
                    OfferingService.getAllOfferings(),
                    listGroupsAll(),
                ]);
                setTools(toolResponse.data);
                setOfferings(offeringResponse.data);
                setGroups(groupsResponse || []);
            } catch (error) {
                console.error('Veri çekilirken hata oluştu:', error);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (visible) {
            if (isEdit && initialData) {
                const { offering, offeringId, toolId, group, groupId, ...rest } = initialData;
                form.setFieldsValue({
                    ...rest,
                    offeringId: (offering && offering.id) ?? offeringId,
                    toolId: toolId,
                    groupId: (group && group.id) ?? groupId,
                    bulkReportEntry: rest.bulkReportEntry ?? false,
                    ...resolveParameterAccreditationValues(rest),
                });
            } else {
                form.resetFields();
                form.setFieldsValue({
                    isDefault: false,
                    countable: true,
                    bulkReportEntry: false,
                    ...PARAMETER_ACCREDITATION_DEFAULTS,
                    unit: defaultUnit,
                    period: defaultPeriod,
                });
            }
        }
    }, [visible, isEdit, initialData, form]);

    const handleFinish = (values: any) => {
        // Checkbox isDefault da payload'a eklenecek
        const payload = { ...initialData, ...values };
        onSave(payload, isEdit ? 'edit' : 'add');
        if (handleSave) {
            handleSave();
        }
        form.resetFields();
        onClose();
    };

    const handleClose = () => {
        form.resetFields();
        onClose();
    };

    return (
        <Modal
            title={isEdit ? 'Parametre Düzenle' : 'Yeni Parametre Ekle'}
            visible={visible}
            onCancel={handleClose}
            footer={null}
            destroyOnClose
        >
            <Form form={form} layout="vertical" onFinish={handleFinish}>
                <Form.Item
                    name="name"
                    label="Adı"
                    rules={[{ required: true, message: 'Lütfen parametre adı giriniz.' }]}
                >
                    <Input placeholder="Adı" />
                </Form.Item>



                <Form.Item
                    name="description"
                    label="Açıklama"
                    rules={[
                        { max: 1000, message: 'Açıklama en fazla 1000 karakter olabilir' },
                    ]}
                >
                    <Input.TextArea placeholder="Açıklama" />
                </Form.Item>

                <Form.Item
                    name="offeringId"
                    label="Hizmet Grubu"
                    rules={[{ required: true, message: 'Lütfen bir hizmet grubu seçiniz.' }]}
                >
                    <Select placeholder="Hizmet Grubu Seçiniz" allowClear>
                        {offerings.map((offering) => (
                            <Option key={offering.id} value={offering.id}>
                                {offering.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="groupId"
                    label="Kategori"
                >
                    <Select placeholder="Kategori Seçiniz" allowClear>
                        {groupOptions.map((g: any) => (
                            <Option key={g.id} value={g.id}>
                                {g.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item name="toolId" label="Bileşen">
                    <Select placeholder="Bileşen Seçiniz" allowClear>
                        {tools.map((tool) => (
                            <Option key={tool.id} value={tool.id}>
                                {tool.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Fiyat"
                    name="price"
                    rules={[{ required: true, message: 'Lütfen fiyat giriniz' }]}
                >
                    <InputNumber
                        min={0}
                        step={0.01}
                        precision={2}
                        style={{ width: '100%' }}
                    />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="unit"
                            label="Birim"
                            rules={[{ required: true, message: 'Lütfen bir birim seçiniz.' }]}
                        >
                            <Select placeholder="Birim Seçiniz">
                                {PARAMETER_UNIT_OPTIONS.map((option) => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="period"
                            label="Azami Süre"
                            rules={[{ required: true, message: 'Lütfen bir Azami Süre seçiniz.' }]}
                        >
                            <Select placeholder="Azami Süre Seçiniz">
                                {PARAMETER_PERIOD_OPTIONS.map((option) => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Saha Kontrol Dk"
                            name="fieldControlTime"
                            rules={[{ required: true, message: 'Lütfen süre giriniz' }]}
                        >
                            <InputNumber
                                min={0}
                                step={0.01}
                                precision={2}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Rapor Hazırlık Dk"
                            name="reportPreparationTime"
                            rules={[{ required: true, message: 'Lütfen süre giriniz' }]}
                        >
                            <InputNumber
                                min={0}
                                step={0.01}
                                precision={2}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Birincil (isDefault) checkbox */}
                <Form.Item
                    name="isDefault"
                    valuePropName="checked"
                >
                    <Checkbox>Birincil</Checkbox>
                </Form.Item>

                <Form.Item
                    name="countable"
                    valuePropName="checked"
                >
                    <Checkbox>Sayılabilir</Checkbox>
                </Form.Item>

                <Form.Item
                    name="bulkReportEntry"
                    label="Toplu Rapor Girişi"
                    valuePropName="checked"
                >
                    <Switch checkedChildren="Açık" unCheckedChildren="Kapalı" />
                </Form.Item>

                <Form.Item
                    name="accreditationRequired"
                    label="Akreditasyon zorunlu mu?"
                    valuePropName="checked"
                >
                    <Switch checkedChildren="Evet" unCheckedChildren="Hayır" />
                </Form.Item>

                <Form.Item
                    name="accredited"
                    label="Akredite miyiz?"
                    valuePropName="checked"
                >
                    <Switch checkedChildren="Evet" unCheckedChildren="Hayır" />
                </Form.Item>

                <Form.Item>
                    <Row justify={'center'}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            className="d-button dark-button width-auto"
                        >
                            Kaydet
                        </Button>
                    </Row>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ParameterModal;
